import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
    SiteFragmentFragment,
    TotadminPermission,
    useDeliveryPlanningUpdatedSubscription,
    useGetAllRegularTotemClosedDayQuery,
    useGetDeliveryPlanningForThreeWeeksQuery,
    useUpdateDeliveryPlanningBatchMutation,
} from 'data/__generated__';
import { formatDateAsAnniversary, getNextMondayDate } from 'helpers/dateTimes';
import { SectionContainer } from 'components/DetailsView/Section';
import { Loader } from 'components/Loader';
import { DeliveryPlanningTable } from 'pages/DeliveryPlanning/components/DeliveryPlanningTable';
import { DeliveryPlanningFilters } from 'pages/DeliveryPlanning/components/DeliveryPlanningFilters';
import {
    filterThreeWeeksBySelectedDays,
    getMondayOfWeek,
    getThreeWeeksDates,
    getThreeWeeksLabels,
    isDateClosed,
} from 'pages/DeliveryPlanning/utils/dateUtils';

type Props = {
    siteDetails: Pick<SiteFragmentFragment, '_id' | 'closingDatesRanges' | 'launchDate' | 'removalDate'>;
};

type SiteState = Record<string, boolean>;

// Les hooks vivent dans le contenu : sans `DeliveryPlanningRead`, la section est verrouillée et rien n'est
// monté — ni requête ni souscription, donc pas d'« Accès refusé ».
export const SiteDeliveryPlanningSection: React.FC<Props> = ({ siteDetails }) => (
    <SectionContainer
        title="Planning de livraison effectif"
        isInitiallyOpen
        requiredPermissions={[TotadminPermission.DeliveryPlanningRead]}
    >
        <SiteDeliveryPlanningContent siteDetails={siteDetails} />
    </SectionContainer>
);

const SiteDeliveryPlanningContent: React.FC<Props> = ({ siteDetails }) => {
    const siteId = siteDetails._id;
    const [selectedDate, setSelectedDateRaw] = useState<Date>(getMondayOfWeek(getNextMondayDate()));
    const [selectedDays, setSelectedDays] = useState<boolean[]>([true, true, true, true, true, false, false]);
    const [sortConfig, setSortConfig] = useState<{ date: string; direction: 'asc' | 'desc' } | null>(null);
    const [planningState, setPlanningState] = useState<SiteState>({});
    const [initialState, setInitialState] = useState<SiteState>({});

    const setSelectedDate = useCallback((date: Date) => setSelectedDateRaw(getMondayOfWeek(date)), []);

    const threeWeeksData = useMemo(() => getThreeWeeksDates(selectedDate), [selectedDate]);
    const threeWeeksLabels = useMemo(() => getThreeWeeksLabels(selectedDate), [selectedDate]);
    const { visibleDates, visibleLabels, weekDelimiters } = useMemo(
        () => filterThreeWeeksBySelectedDays(threeWeeksData, threeWeeksLabels, selectedDays),
        [threeWeeksData, threeWeeksLabels, selectedDays],
    );

    const mondayDateString = formatDateAsAnniversary({ dateTime: selectedDate });

    const {
        data: planningData,
        loading: planningLoading,
        refetch: refetchPlanning,
    } = useGetDeliveryPlanningForThreeWeeksQuery({
        variables: { siteId, mondayDate: mondayDateString },
        fetchPolicy: 'cache-and-network',
    });

    const { data: closedDaysData } = useGetAllRegularTotemClosedDayQuery({ fetchPolicy: 'cache-and-network' });

    useDeliveryPlanningUpdatedSubscription({
        variables: { siteIds: [siteId] },
        onData: () => {
            refetchPlanning();
        },
    });

    const [updateDeliveryPlanningBatch, { loading: saveLoading }] = useUpdateDeliveryPlanningBatchMutation();

    // Re-sync local state when the query reloads (week change, refetch after site save, etc.)
    useEffect(() => {
        if (!planningData?.getDeliveryPlanningForThreeWeeks) return;
        const next: SiteState = {};
        threeWeeksData.allDates.forEach((d) => {
            next[d] = false;
        });
        planningData.getDeliveryPlanningForThreeWeeks.deliveryPlanning.forEach((entry) => {
            if (threeWeeksData.allDates.includes(entry.date)) {
                next[entry.date] = entry.isActive;
            }
        });
        setPlanningState(next);
        setInitialState(next);
    }, [planningData, threeWeeksData.allDates]);

    // Closure days on the site may have been edited on the parent form — refetch when they change.
    const closingKey = useMemo(
        () => siteDetails.closingDatesRanges.map(({ start, end }) => `${start}|${end}`).join(','),
        [siteDetails.closingDatesRanges],
    );
    // Skip the first run: the query already fetches on mount (cache-and-network), only refetch on actual changes.
    const isFirstClosingKeyRun = useRef(true);
    useEffect(() => {
        if (isFirstClosingKeyRun.current) {
            isFirstClosingKeyRun.current = false;
            return;
        }
        refetchPlanning();
    }, [closingKey, refetchPlanning]);

    const sitePlannings = planningData?.getDeliveryPlanningForThreeWeeks
        ? [planningData.getDeliveryPlanningForThreeWeeks]
        : [];
    const totemClosedDays = closedDaysData?.getAllRegularTotemClosedDay || [];

    const formStateForTable = useMemo(() => ({ [siteId]: planningState }), [siteId, planningState]);

    const handleDeliveryPlanningUpdate = useCallback((_siteId: string, date: string) => {
        setPlanningState((prev) => ({ ...prev, [date]: !prev[date] }));
    }, []);

    const handleSelectAllForDay = useCallback(
        (date: string, isChecked: boolean) => {
            const isClosed = isDateClosed(
                date,
                siteDetails.closingDatesRanges,
                totemClosedDays,
                siteDetails.launchDate,
                siteDetails.removalDate,
            );
            if (isClosed) return;
            setPlanningState((prev) => ({ ...prev, [date]: isChecked }));
        },
        [siteDetails.closingDatesRanges, siteDetails.launchDate, siteDetails.removalDate, totemClosedDays],
    );

    const handleDaySelect = useCallback((dayIndex: number) => {
        setSelectedDays((prev) => prev.map((v, i) => (i === dayIndex ? !v : v)));
    }, []);

    const handleSort = useCallback((date: string) => {
        setSortConfig((prev) => {
            if (prev?.date === date) {
                return prev.direction === 'asc' ? { date, direction: 'desc' } : null;
            }
            return { date, direction: 'asc' };
        });
    }, []);

    const dirtyChanges = useMemo(() => {
        const changes: Array<{ siteId: string; date: string; isActive: boolean }> = [];
        Object.entries(planningState).forEach(([date, isActive]) => {
            if ((initialState[date] ?? false) !== isActive) {
                changes.push({ siteId, date, isActive });
            }
        });
        return changes;
    }, [planningState, initialState, siteId]);

    const onSave = useCallback(async () => {
        if (dirtyChanges.length === 0) return;
        try {
            const result = await updateDeliveryPlanningBatch({
                variables: { deliveryPlanningBatchUpdate: dirtyChanges },
            });
            const payload = result.data?.updateDeliveryPlanningBatch;
            if (payload?.success) {
                toast.success(`${dirtyChanges.length} modification(s) appliquée(s)`);
                await refetchPlanning();
            } else {
                toast.error(`Erreur lors de la sauvegarde`);
            }
        } catch (error) {
            toast.error(`Erreur lors de la sauvegarde: ${error}`);
        }
    }, [dirtyChanges, updateDeliveryPlanningBatch, refetchPlanning]);

    return (
        <FullWidthContent>
            <FilterRow>
                <DeliveryPlanningFilters
                    selectedDate={selectedDate}
                    selectedDays={selectedDays}
                    hasChanges={dirtyChanges.length > 0}
                    onDateChange={setSelectedDate}
                    onDaySelect={handleDaySelect}
                    onSave={onSave}
                    saveLoading={saveLoading}
                    showBackButton={false}
                />
            </FilterRow>
            {planningLoading && !sitePlannings ? (
                <Loader />
            ) : (
                <DeliveryPlanningTable
                    sitePlannings={sitePlannings}
                    visibleDates={visibleDates}
                    visibleLabels={visibleLabels}
                    weekDelimiters={weekDelimiters}
                    deliveryPlanningState={formStateForTable}
                    totemClosedDays={totemClosedDays}
                    sortConfig={sortConfig}
                    onDeliveryPlanningUpdate={handleDeliveryPlanningUpdate}
                    onSelectAllForDay={handleSelectAllForDay}
                    onSort={handleSort}
                />
            )}
        </FullWidthContent>
    );
};

const FullWidthContent = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const FilterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    flex-wrap: wrap;
`;
